import { takeEvery, fork, put, all, call } from "redux-saga/effects"

// Redux Saga types
import {
  GET_ALL_PRODUCT_CATEGORIES_REQUEST,
  GET_ALL_PRODUCT_CATEGORIES_SUCCESS,
  GET_ALL_PRODUCT_CATEGORIES_FAIL,
  GET_PRODUCT_CATEGORY_REQUEST,
  GET_PRODUCT_CATEGORY_SUCCESS,
  GET_PRODUCT_CATEGORY_FAIL,
  CREATE_PRODUCT_CATEGORY_REQUEST,
  CREATE_PRODUCT_CATEGORY_SUCCESS,
  CREATE_PRODUCT_CATEGORY_FAIL,
  UPDATE_PRODUCT_CATEGORY_REQUEST,
  UPDATE_PRODUCT_CATEGORY_SUCCESS,
  UPDATE_PRODUCT_CATEGORY_FAIL,
  DELETE_PRODUCT_CATEGORY_REQUEST,
  DELETE_PRODUCT_CATEGORY_SUCCESS,
  DELETE_PRODUCT_CATEGORY_FAIL,
  TOGGLE_PRODUCT_CATEGORY_STATUS_REQUEST,
  TOGGLE_PRODUCT_CATEGORY_STATUS_SUCCESS,
  TOGGLE_PRODUCT_CATEGORY_STATUS_FAIL,
  GET_CATEGORY_PRODUCTS_REQUEST,
  GET_CATEGORY_PRODUCTS_SUCCESS,
  GET_CATEGORY_PRODUCTS_FAIL
} from "./actionTypes"

// Product Categories actions
import {
  getAllProductCategoriesSuccess,
  getAllProductCategoriesFail,
  getProductCategorySuccess,
  getProductCategoryFail,
  createProductCategorySuccess,
  createProductCategoryFail,
  updateProductCategorySuccess,
  updateProductCategoryFail,
  deleteProductCategorySuccess,
  deleteProductCategoryFail,
  toggleProductCategoryStatusSuccess,
  toggleProductCategoryStatusFail,
  getCategoryProductsSuccess,
  getCategoryProductsFail
} from "./actions"

// Import API functions from backend_helper
import {
  getAllProductCategories,
  getProductCategoryById,
  createProductCategory,
  updateProductCategory,
  deleteProductCategory,
  toggleProductCategoryStatus,
  getProductsByCategoryId
} from "../../helpers/backend_helper"

// Toast for notifications
import { toast } from "react-toastify"

// Get all product categories
function* fetchAllProductCategories({ payload: { history } }) {
  try {
    const response = yield call(getAllProductCategories)
    yield put({ type: GET_ALL_PRODUCT_CATEGORIES_SUCCESS, payload: response })
  } catch (error) {
    yield put({ type: GET_ALL_PRODUCT_CATEGORIES_FAIL, payload: error })
    toast.error("Failed to load product categories")
  }
}

// Get single product category
function* fetchProductCategory({ payload: { categoryId, history } }) {
  try {
    const response = yield call(getProductCategoryById, categoryId)
    yield put({ type: GET_PRODUCT_CATEGORY_SUCCESS, payload: response })
  } catch (error) {
    yield put({ type: GET_PRODUCT_CATEGORY_FAIL, payload: error })
    toast.error("Failed to load product category details")
  }
}

// Create product category
function* onCreateProductCategory({ payload: { categoryData, history } }) {
  try {
    const response = yield call(createProductCategory, categoryData)
    yield put({ type: CREATE_PRODUCT_CATEGORY_SUCCESS, payload: response })
    toast.success("Product category created successfully!")
    // Refresh the categories list
    yield put({ type: GET_ALL_PRODUCT_CATEGORIES_REQUEST, payload: { history } })
  } catch (error) {
    yield put({ type: CREATE_PRODUCT_CATEGORY_FAIL, payload: error })
    toast.error("Failed to create product category")
  }
}

// Update product category
function* onUpdateProductCategory({ payload: { categoryId, categoryData, history } }) {
  try {
    const response = yield call(updateProductCategory, categoryId, categoryData)
    yield put({ type: UPDATE_PRODUCT_CATEGORY_SUCCESS, payload: response })
    toast.success("Product category updated successfully!")
    // Refresh the categories list
    yield put({ type: GET_ALL_PRODUCT_CATEGORIES_REQUEST, payload: { history } })
  } catch (error) {
    yield put({ type: UPDATE_PRODUCT_CATEGORY_FAIL, payload: error })
    toast.error("Failed to update product category")
  }
}

// Delete product category
function* onDeleteProductCategory({ payload: { categoryId, mode, moveToCategoryId, history } }) {
  try {
    const response = yield call(deleteProductCategory, categoryId, mode, moveToCategoryId)
    yield put({ type: DELETE_PRODUCT_CATEGORY_SUCCESS, payload: categoryId })
    toast.success("Product category deleted successfully!")
    // Refresh the categories list
    yield put({ type: GET_ALL_PRODUCT_CATEGORIES_REQUEST, payload: { history } })
  } catch (error) {
    yield put({ type: DELETE_PRODUCT_CATEGORY_FAIL, payload: error })
    toast.error("Failed to delete product category")
  }
}

// Toggle product category status
function* onToggleProductCategoryStatus({ payload: { categoryId, history } }) {
  try {
    const response = yield call(toggleProductCategoryStatus, categoryId)
    yield put({ type: TOGGLE_PRODUCT_CATEGORY_STATUS_SUCCESS, payload: response })
    toast.success(response.message || "Category status updated successfully!")
    // Refresh the categories list
    yield put({ type: GET_ALL_PRODUCT_CATEGORIES_REQUEST, payload: { history } })
  } catch (error) {
    yield put({ type: TOGGLE_PRODUCT_CATEGORY_STATUS_FAIL, payload: error })
    toast.error("Failed to toggle category status")
  }
}

// Get products from category
function* fetchCategoryProducts({ payload: { categoryId, history } }) {
  try {
    const response = yield call(getProductsByCategoryId, categoryId)
    yield put({ type: GET_CATEGORY_PRODUCTS_SUCCESS, payload: response })
  } catch (error) {
    yield put({ type: GET_CATEGORY_PRODUCTS_FAIL, payload: error })
    toast.error("Failed to load products from category")
  }
}

// Watchers
function* watchGetAllProductCategories() {
  yield takeEvery(GET_ALL_PRODUCT_CATEGORIES_REQUEST, fetchAllProductCategories)
}

function* watchGetProductCategory() {
  yield takeEvery(GET_PRODUCT_CATEGORY_REQUEST, fetchProductCategory)
}

function* watchCreateProductCategory() {
  yield takeEvery(CREATE_PRODUCT_CATEGORY_REQUEST, onCreateProductCategory)
}

function* watchUpdateProductCategory() {
  yield takeEvery(UPDATE_PRODUCT_CATEGORY_REQUEST, onUpdateProductCategory)
}

function* watchDeleteProductCategory() {
  yield takeEvery(DELETE_PRODUCT_CATEGORY_REQUEST, onDeleteProductCategory)
}

function* watchToggleProductCategoryStatus() {
  yield takeEvery(TOGGLE_PRODUCT_CATEGORY_STATUS_REQUEST, onToggleProductCategoryStatus)
}

function* watchGetCategoryProducts() {
  yield takeEvery(GET_CATEGORY_PRODUCTS_REQUEST, fetchCategoryProducts)
}

function* productCategoriesSaga() {
  yield all([
    fork(watchGetAllProductCategories),
    fork(watchGetProductCategory),
    fork(watchCreateProductCategory),
    fork(watchUpdateProductCategory),
    fork(watchDeleteProductCategory),
    fork(watchToggleProductCategoryStatus),
    fork(watchGetCategoryProducts)
  ])
}

export default productCategoriesSaga